/**
 * SEO HEADING STRUCTURE - Study With Me
 * =====================================
 * 
 * TITLE: Study With Me - Solo Focus Sessions | CSW | Online Workspace...
 * META DESCRIPTION: Study with me live sessions. Join solo focus sessions...
 * 
 * H1 (1 tag):
 *   - "Study With Me Live" (hero section)
 * 
 * H2 (3 tags):
 *   - "Everything You Need to Stay Focused" (features section)
 *   - "Why Study With Me?" (benefits section)
 *   - "Ready to Start Studying?" (CTA section)
 * 
 * H3 (8 tags):
 *   - Feature card titles (4): "Pomodoro Timer", "Task Management", etc.
 *   - Benefit titles (4): Dynamic benefit items
 */

import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Clock, ListTodo, Music, Focus, ArrowRight, Play } from "lucide-react";

const StudyWithMe = () => {
  const features = [
    {
      icon: <Clock className="w-8 h-8" />,
      title: "Pomodoro Timer",
      description: "Stay focused with customizable work and break intervals",
    },
    {
      icon: <ListTodo className="w-8 h-8" />,
      title: "Task Management",
      description: "Track your progress with an integrated to-do list",
    },
    {
      icon: <Music className="w-8 h-8" />,
      title: "Focus Music",
      description: "Curated playlists to enhance your concentration",
    },
    {
      icon: <Focus className="w-8 h-8" />,
      title: "Distraction-Free",
      description: "Minimalist interface designed for deep work",
    },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEO 
        title="Study With Me - Solo Focus Sessions"
        description="Study with me live sessions. Join solo focus sessions with Pomodoro timer, task management, and focus music. Stay productive and motivated."
        keywords="study with me, study with me live, solo study, focus session, study session, study alone online"
        canonicalUrl="/study-with-me/"
      />
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
          {/* Gradient Background */}
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background to-muted" />
          
          {/* Animated Glow Effects */}
          <div className="absolute top-20 left-20 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] animate-glow-pulse" style={{ animationDelay: '1s' }} />
          
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <div className="text-left space-y-8 animate-fade-in">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card/50 border border-border backdrop-blur-sm">
                  <div className="w-2 h-2 bg-primary rounded-full animate-glow-pulse" />
                  <span className="text-sm font-medium text-muted-foreground">Solo Focus Sessions</span>
                </div>
                
                <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight">
                  <span className="text-gradient">Study With Me Live</span>
                </h1>
                
                <p className="text-xl sm:text-2xl text-muted-foreground font-light max-w-xl">
                  Your personal productivity space. Focus on your goals with tools designed to keep you motivated. <a href="/study-stream/why-studying-on-camera-helps/" className="text-primary hover:underline text-lg">Learn why studying on camera helps →</a>
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <Button 
                    size="lg" 
                    className="group bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105"
                    asChild
                  >
                    <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                      Start Solo Session
                      <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </a>
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline"
                    className="border-2 border-border hover:bg-card font-semibold px-8 py-6 text-lg transition-all hover:scale-105"
                    asChild
                  >
                    <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                      <Play className="mr-2 h-5 w-5" />
                      Watch Demo
                    </a>
                  </Button>
                </div>
                
                <div className="flex items-center gap-8 pt-8">
                  <div>
                    <p className="text-3xl font-bold text-gradient">25min</p>
                    <p className="text-sm text-muted-foreground">Focus Sessions</p>
                  </div>
                  <div className="w-px h-12 bg-border" />
                  <div>
                    <p className="text-3xl font-bold text-gradient">3 Modes</p>
                    <p className="text-sm text-muted-foreground">Chill, Study, Work</p>
                  </div>
                  <div className="w-px h-12 bg-border" />
                  <div>
                    <p className="text-3xl font-bold text-gradient">Free</p>
                    <p className="text-sm text-muted-foreground">Forever</p>
                  </div>
                </div>
              </div>
              
              {/* Right Video */}
              <div className="relative animate-fade-in" style={{ animationDelay: '0.2s' }}>
                <div className="relative rounded-3xl overflow-hidden border border-border/50 shadow-2xl">
                  <video 
                    src="/videos/productivity-demo.mp4" 
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-full h-auto"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                </div>
                
                {/* Floating Card */}
                <div className="absolute bottom-8 left-8 right-8 bg-card/90 backdrop-blur-md border border-border rounded-2xl p-6 shadow-xl">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Your Personal Space</p>
                      <p className="text-xl font-bold text-foreground">Focus & Achieve</p>
                    </div>
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                      <Focus className="w-6 h-6 text-primary-foreground" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-24 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-muted to-background" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[150px]" />
          
          <div className="max-w-6xl mx-auto relative z-10">
            <div className="text-center mb-16 animate-fade-in">
              <h2 className="text-4xl md:text-5xl font-bold font-display mb-6">
                Everything You Need to{" "}
                <span className="text-gradient">Focus</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Powerful tools designed to help you stay productive and achieve your goals
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.map((feature, index) => (
                <Card 
                  key={index} 
                  className="text-center bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105 hover:shadow-xl group"
                >
                  <CardHeader>
                    <div className="mx-auto mb-4 text-primary group-hover:scale-110 transition-transform">
                      {feature.icon}
                    </div>
                    <CardTitle className="text-xl font-display">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-muted-foreground">{feature.description}</CardDescription>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-24 px-4 relative">
          <div className="absolute top-0 right-0 w-96 h-96 bg-secondary/10 rounded-full blur-[120px]" />
          
          <div className="max-w-4xl mx-auto relative z-10">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold font-display mb-6">
                Your Personal{" "}
                <span className="text-gradient">Study Flow</span>
              </h2>
              <p className="text-xl text-muted-foreground">
                Three simple steps to achieve deep focus
              </p>
            </div>
            
            <div className="space-y-8">
              {[
                {
                  step: 1,
                  title: "Set Your Goals",
                  description: "Create your task list and prioritize what needs to be done"
                },
                {
                  step: 2,
                  title: "Choose Your Mode",
                  description: "Select from Chill, Study, or Work mode to match your energy"
                },
                {
                  step: 3,
                  title: "Start Your Session",
                  description: "Begin your Pomodoro timer and immerse yourself in focused work"
                }
              ].map((item, index) => (
                <div 
                  key={index}
                  className="flex gap-6 items-start p-6 rounded-2xl bg-card/50 backdrop-blur-sm border border-border/50 hover:border-primary/50 transition-all hover:shadow-lg group"
                >
                  <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-secondary text-primary-foreground flex items-center justify-center font-bold text-xl font-display group-hover:scale-110 transition-transform">
                    {item.step}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2 font-display">{item.title}</h3>
                    <p className="text-muted-foreground">
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 px-4 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-background via-primary/5 to-background" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-primary/20 rounded-full blur-[150px]" />
          
          <div className="max-w-4xl mx-auto text-center relative z-10">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 font-display">
              Ready to{" "}
              <span className="text-gradient">Focus</span>?
            </h2>
            <p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto">
              Start your personal study session now. No registration required.
            </p>
            <Button 
              size="lg" 
              className="group bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-10 py-7 text-lg glow-primary transition-all hover:scale-105"
              asChild
            >
              <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                Launch Study Space
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </a>
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default StudyWithMe;
